<template>
  <base-tabs v-if="activeTabKey"
             :tabs="tabs"
             v-model="activeTabKey"
             class="h-full theme-tabs"
             tab-classes="flex"
             input-placeholder="Type theme name"
             :is-handling-route-change="isHandlingRouteChange"
             @update:tab-label="updateThemeName"
  >
    <template #tab>
      <transition name="fade" mode="out-in">
        <div class="grid grid-cols-12 h-full">
          <SettingsBotThemeEditor :key="activeTabKey"
                                  class="col-span-8 p-8 pt-4 min-h-0 h-full overflow-hidden"
                                  v-model="themeValue"
                                  :image-cache.sync="imageCacheValue"
                                  @update:json-validity="$emit('update:json-validity', $event)"
          />
          <SettingsBotThemePreview class="col-span-4 p-8 pt-4"
                                   :image-cache="imageCacheValue"
                                   :theme="themeValue"
          />
        </div>
      </transition>
    </template>
    <template #tab-suffix="{ tabKey }">
      <side-list-menu v-if="tabKey === activeTabKey && tabKey !== ITEM_IDS.NEW"
                      :items="activeTabMenuItems"
                      class="text-primary"
                      @rename="startRename"
                      @delete="$emit('start-delete', activeTabKey)"
                      @copy-chat-url="copyChatUrl"
      />
      <button v-if="lastTheme.id === tabKey && !isAddingNewThemeValue"
              class="absolute inset-x-full top-1 flex items-center border-0 bg-white pl-1 text-gray-dark hover:text-primary"
              @click.prevent.stop="isAddingNewThemeValue = true"
      >
        <base-icon icon="add" size="20px"/>
      </button>
      <button v-if="tabKey === ITEM_IDS.NEW && lastTheme.id !== tabKey"
              class="flex items-center border-0 bg-white pl-1 text-gray-dark hover:text-primary"
              @click.prevent.stop="handleCancelNewTheme"
      >
        <base-icon icon="close" size="20px"/>
      </button>
    </template>
  </base-tabs>
</template>

<script>
import BaseButton from '@/components/base-components/BaseButton.vue'
import BaseIcon from '@/components/base-components/BaseIcon.vue'
import BaseTabs from '@/components/base-components/BaseTabs.vue'
import JsonInput from '@/components/base-components/JsonInput.vue'
import SettingsBotThemeEditor from '@/components/settings/bot-theme/SettingsBotThemeEditor.vue'
import SettingsBotThemePreview from '@/components/settings/bot-theme/SettingsBotThemePreview.vue'
import SideListMenu from '@/components/common/SideListMenu.vue'
import channelModule from '@/store/modules/channel.module'
import find from 'ramda/src/find'
import last from 'ramda/src/last'
import map from 'ramda/src/map'
import propEq from 'ramda/src/propEq'
import toaster from '@/utils/toaster'
import { CHANNEL_TYPES } from '@/utils/channel.constants'
import { ITEM_IDS, MENU_ITEMS } from '@/utils/editor.constants'
import { AMIO_DEMO_CHAT_URL } from '@/constants/chat-theme.constants'
import isNil from 'ramda/src/isNil'

export default {
  name: 'SettingsBotThemeTabs',

  components: {
    BaseButton,
    BaseIcon,
    BaseTabs,
    JsonInput,
    SettingsBotThemeEditor,
    SettingsBotThemePreview,
    SideListMenu
  },

  props: {
    themes: {
      type: Array,
      required: true
    },
    value: {
      type: String
    },
    theme: {
      type: Object,
      default: () => {}
    },
    isAddingNewTheme: {
      type: Boolean,
      required: true
    },
    imageCache: {
      type: Object,
      required: true
    },
    isHandlingRouteChange: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      MENU_ITEMS,
      ITEM_IDS
    }
  },

  emits: [
    'input',
    'start-delete',
    'start-adding',
    'start-rename',
    'rename-theme',
    'update:theme',
    'update:json-validity',
    'update:image-cache'
  ],

  watch: {
    themeId() {
      this.imageCacheValue = {
        buttonIcon: null,
        headerLogo: null
      }
    }
  },

  computed: {
    imageCacheValue: {
      get() {
        return this.imageCache
      },
      set(newImageCache) {
        this.$emit('update:image-cache', newImageCache)
      }
    },

    tabs() {
      return map(a => ({ key: a.id, label: a.name, isEdited: a.isEdited }), this.themes)
    },

    activeTabKey: {
      get() {
        return this.value
      },
      set(tabKey) {
        if (tabKey === ITEM_IDS.NEW) return
        this.$emit('input', tabKey)
      }
    },

    isAddingNewThemeValue: {
      get() {
        return this.isAddingNewTheme
      },
      set(isAddingNewTheme) {
        this.$emit('start-adding', isAddingNewTheme)
      }
    },

    themeValue: {
      get() {
        return this.theme
      },
      set({ name, themeConfig, langConfig, launcherImageUrl }) {
        if (themeConfig) {
          this.$emit('update:theme', { themeConfig })
        }
        if (!isNil(name)) {
          this.$emit('rename-theme', { name, id: this.themeId })
        }
        if (langConfig) {
          this.$emit('update:theme', { langConfig })
        }
        if (!isNil(launcherImageUrl)) {
          this.$emit('update:theme', { launcherImageUrl })
        }
      }
    },

    themeId() {
      return this.themeValue.id
    },

    lastTheme() {
      return last(this.themes)
    },

    activeTabMenuItems() {
      const baseItems = [MENU_ITEMS.rename, MENU_ITEMS.copyChatUrl]
      if (this.themes.length === 1) {
        return baseItems
      }

      return [...baseItems, MENU_ITEMS.delete]
    },

    amioChannel() {
      return find(propEq('type', CHANNEL_TYPES.AMIO_CHAT))(channelModule.getChannelList())
    }
  },

  methods: {
    startRename() {
      this.$emit('start-rename', this.activeTabKey)
    },

    copyChatUrl() {
      this.$copyText(
        `${AMIO_DEMO_CHAT_URL}/?channelId=
             ${this.amioChannel.id}&startOpened=true&themeOverride=
             ${encodeURIComponent(JSON.stringify(this.themeValue))}`
      )
      toaster.success('Chat URL copied!', { position: 'top-right', timeout: 2000 })
    },

    updateThemeName(e) {
      if(e.key === ITEM_IDS.NEW && !e.label && !this.isHandlingRouteChange) {
        this.isAddingNewThemeValue = false
        return
      }
      this.themeValue = { name: e.label }
    },

    handleCancelNewTheme() {
      this.isAddingNewThemeValue = false
    }
  }
}
</script>

<style lang="scss">
.theme-tabs {
  div.tabs-header {
    @apply border-0 #{!important};
    @apply bg-transparent #{!important};
    @apply rounded-none #{!important};
    @apply pb-0 px-8 mb-0 #{!important};
    @apply overflow-auto #{!important};

    &::-webkit-scrollbar {
      @apply hidden;
    }

    div {
      @apply flex items-center #{!important};
    }

    .selected-tab {
      @apply border-solid border-primary border-2 border-t-0 border-l-0 border-r-0 #{!important};
      @apply rounded-none gap-1 z-20 #{!important};

      .vs-con-dropdown {
        @apply w-6 h-6 text-primary #{!important};

        .vs-icon {
          @apply text-primary #{!important};
        }
      }
    }

    button {
      @apply flex items-center justify-center w-max #{!important};

      span {
        @apply w-max pb-2 py-1 #{!important};

        &.text-black {
          @apply text-primary #{!important};
        }
      }
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter, .fade-leave-to {
  @apply opacity-0;
}
</style>
